import { Link } from "react-router-dom";
import { Linkedin, Twitter, Instagram, Facebook, MapPin, Mail, Phone } from "lucide-react";
import Logo from "@/components/shared/Logo";
import { COMPANY } from "@/data/site";
import { FEATURED_SERVICES } from "@/data/services";
import { FEATURED_INDUSTRIES } from "@/data/industries";

const colClass = "flex flex-col gap-3";
const linkClass = "text-sm text-[#A1A1AA] hover:text-white transition-colors";
const headClass = "text-xs font-bold uppercase tracking-[0.2em] text-white mb-2";

export default function Footer() {
  return (
    <footer className="bg-[#111111] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-10">
          <div className="col-span-2">
            <Logo variant="light" />
            <p className="text-sm text-[#A1A1AA] mt-5 max-w-xs leading-relaxed">
              India&apos;s leading Generative Engine Optimization consulting company. We make brands visible,
              trusted and recommended across AI search engines.
            </p>
            <div className="flex gap-3 mt-6">
              {[
                { icon: Linkedin, href: COMPANY.social.linkedin, label: "LinkedIn" },
                { icon: Twitter, href: COMPANY.social.twitter, label: "Twitter" },
                { icon: Instagram, href: COMPANY.social.instagram, label: "Instagram" },
                { icon: Facebook, href: COMPANY.social.facebook, label: "Facebook" },
              ].map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="h-9 w-9 rounded-full border border-white/15 flex items-center justify-center text-[#A1A1AA] hover:text-white hover:border-white/40 transition-colors"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div className={colClass}>
            <p className={headClass}>Services</p>
            {FEATURED_SERVICES.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className={linkClass}>{s.title}</Link>
            ))}
            <Link to="/services" className="text-sm text-[#0B63F6] hover:text-white transition-colors">All services →</Link>
          </div>

          <div className={colClass}>
            <p className={headClass}>Industries</p>
            {FEATURED_INDUSTRIES.map((i) => (
              <Link key={i.slug} to={`/industries/${i.slug}`} className={linkClass}>{i.title.replace("GEO for ", "")}</Link>
            ))}
            <Link to="/industries" className="text-sm text-[#0B63F6] hover:text-white transition-colors">All industries →</Link>
          </div>

          <div className={colClass}>
            <p className={headClass}>Company</p>
            <Link to="/about" className={linkClass}>About</Link>
            <Link to="/tools" className={linkClass}>AI Visibility Checker</Link>
            <Link to="/blog" className={linkClass}>Blog</Link>
            <Link to="/glossary" className={linkClass}>GEO Glossary</Link>
            <Link to="/contact" className={linkClass}>Contact</Link>
          </div>

          <div className={colClass}>
            <p className={headClass}>Contact</p>
            <a href={COMPANY.phoneHref} className={`${linkClass} flex items-start gap-2`}>
              <Phone className="h-4 w-4 mt-0.5 shrink-0" /> {COMPANY.phone}
            </a>
            <a href={`mailto:${COMPANY.email}`} className={`${linkClass} flex items-start gap-2`}>
              <Mail className="h-4 w-4 mt-0.5 shrink-0" /> {COMPANY.email}
            </a>
            <p className={`${linkClass} flex items-start gap-2`}>
              <MapPin className="h-4 w-4 mt-0.5 shrink-0" /> Maninagar, Ahmedabad, Gujarat, India
            </p>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <p className="text-xs text-[#71717A]">
            © {new Date().getFullYear()} CompuBrain AI — a division of CompuBrain. All rights reserved.
          </p>
          <p className="text-xs text-[#71717A]">Generative Engine Optimization · Ahmedabad · India</p>
        </div>
      </div>
    </footer>
  );
}
